import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Modal,
  Slider,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';

import { useCustomSpotsEditor } from '@core/dashboard/components/DashboardContent/CustomSpotsEditor/useCustomSpotsEditor';
import { useMytaverseEvent } from '@core/dashboard/providers';

import { getSpotType } from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useInspectorStyles } from './styles';

type LightboxMode = 'video' | 'image' | null;

type AnySpot = Record<string, unknown> & {
  brandingSpotId?: string;
  ueIdentifier?: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  previewImage?: string;
  distance?: number | null;
  data?: {
    url?: string;
    location?: string;
    previewImage?: string;
  };
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const UE_UNITS_PER_METER = 100;
const DISTANCE_MIN_METERS = 5;
const DISTANCE_MAX_METERS = 50;
const DISTANCE_DEFAULT_METERS = 10;
const DISTANCE_SAVE_DEBOUNCE_MS = 400;

const ueUnitsToMeters = (ueUnits: unknown): number | null => {
  if (typeof ueUnits !== 'number' || !Number.isFinite(ueUnits)) {
    return null;
  }
  const meters = Math.round(ueUnits / UE_UNITS_PER_METER);
  if (meters < DISTANCE_MIN_METERS) {
    return DISTANCE_MIN_METERS;
  }
  if (meters > DISTANCE_MAX_METERS) {
    return DISTANCE_MAX_METERS;
  }
  return meters;
};

const metersToUeUnits = (meters: number): number => meters * UE_UNITS_PER_METER;

const getFilename = (url: string | null): string => {
  if (!url) {
    return '';
  }
  try {
    const u = new URL(url, window.location.href);
    const last = u.pathname.split('/').pop() || '';
    return decodeURIComponent(last);
  } catch {
    const last = url.split('/').pop() || '';
    return decodeURIComponent(last);
  }
};

interface SpotInspectorProps {
  ueIdentifier: string;
}

const SpotInspector = ({ ueIdentifier }: SpotInspectorProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { setAutosaveStatus, clearSelection } = useEditorMode();

  const {
    roomSpots,
    brandingSpots,
    currentBrandingId,
    isBrandingModeEnabled,
    selectedCustomizationSpotType,
    setSelectedCustomizationSpotType,
    selectedCustomizationTag,
    setSelectedCustomizationTag,
    setShowCustomizeSpotModal,
  } = useMytaverseEvent();

  const spot = useMemo<AnySpot | null>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    if (!Array.isArray(source)) {
      return null;
    }
    const selectedUeId = normalizeUeIdentifier(ueIdentifier);
    return (
      (source as AnySpot[]).find(
        (s) => normalizeUeIdentifier(s?.ueIdentifier) === selectedUeId,
      ) ?? null
    );
  }, [
    ueIdentifier,
    currentBrandingId,
    isBrandingModeEnabled,
    brandingSpots,
    roomSpots,
  ]);

  const rawSpotType =
    typeof spot?.type === 'string' ? spot.type.toUpperCase() : '';
  const isVideoStatic =
    selectedCustomizationSpotType === 'VIDEO_STATIC' ||
    rawSpotType === 'VIDEO_STATIC';
  const declaredType = getSpotType(spot ?? {});
  const isVideo =
    isVideoStatic ||
    (selectedCustomizationSpotType || declaredType) === 'VIDEO';

  const controller = useCustomSpotsEditor({
    onClose: () => undefined,
    suppressAssetUpdatedModal: true,
  });

  const {
    isUploading,
    isApplyingToSurface,
    uploadProgress,
    uploadStepMessage,
    fileInputRef,
    videoFileInputRef,
    previewImageFileInputRef,
    handleImageFiles,
    handleVideoFileSelect,
    handlePreviewImageFileSelect,
    displayedPreviewUrl,
    selectedFile,
    selectedVideoFile,
    selectedVideoLocalUrl,
    videoPreviewLocalUrl,
    isVideoReadyToSubmit,
    isImageReadyToUpload,
    handleConfirmImage,
    handleConfirmVideo,
    saveSpotDistance,
    error,
  } = controller;

  const confirmedImageRef = useRef<File | null>(null);
  useEffect(() => {
    if (
      !isVideo &&
      selectedFile &&
      selectedFile !== confirmedImageRef.current &&
      isImageReadyToUpload
    ) {
      confirmedImageRef.current = selectedFile;
      handleConfirmImage();
    }
  }, [isVideo, selectedFile, isImageReadyToUpload, handleConfirmImage]);

  const prevBusyRef = useRef(false);
  useEffect(() => {
    const busy = isUploading || isApplyingToSurface;
    if (busy && !prevBusyRef.current) {
      setAutosaveStatus('saving');
    } else if (!busy && prevBusyRef.current) {
      setAutosaveStatus(error ? 'error' : 'saved');
    }
    prevBusyRef.current = busy;
  }, [isUploading, isApplyingToSurface, error, setAutosaveStatus]);

  const currentUrl = spot?.data?.url || spot?.data?.location || null;
  const previewImageUrl =
    spot?.data?.previewImage || spot?.previewImage || null;
  const filename = getFilename(currentUrl);

  const isVideoSpot = declaredType === 'VIDEO' || isVideoStatic;
  const imageThumbUrl = isVideoSpot ? null : currentUrl;
  const persistedVideoUrl = isVideoSpot ? currentUrl : null;

  const previewThumb = isVideo ? videoPreviewLocalUrl || previewImageUrl : null;
  const imageOnlyThumb = !isVideo
    ? selectedFile
      ? displayedPreviewUrl
      : imageThumbUrl
    : null;

  const videoSrc = isVideo ? selectedVideoLocalUrl || persistedVideoUrl : null;

  const [lightboxMode, setLightboxMode] = useState<LightboxMode>(null);

  useEffect(() => {
    if (isUploading || isApplyingToSurface) {
      setLightboxMode(null);
    }
  }, [isUploading, isApplyingToSurface]);

  const brandingSpotId = spot?.brandingSpotId ?? null;
  const persistedDistanceMeters = ueUnitsToMeters(spot?.distance);
  const hasPersistedDistance = persistedDistanceMeters !== null;

  const [distanceMeters, setDistanceMeters] = useState<number>(
    persistedDistanceMeters ?? DISTANCE_DEFAULT_METERS,
  );
  const [isDistanceInfinite, setIsDistanceInfinite] = useState<boolean>(
    !hasPersistedDistance,
  );

  useEffect(() => {
    const next = ueUnitsToMeters(spot?.distance);
    setDistanceMeters(next ?? DISTANCE_DEFAULT_METERS);
    setIsDistanceInfinite(next === null);
  }, [brandingSpotId, spot?.distance]);

  const distanceSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastSavedDistanceUeRef = useRef<number | null>(
    typeof spot?.distance === 'number' ? spot.distance : null,
  );

  useEffect(() => {
    lastSavedDistanceUeRef.current =
      typeof spot?.distance === 'number' ? spot.distance : null;
  }, [brandingSpotId, spot?.distance]);

  useEffect(() => {
    return () => {
      if (distanceSaveTimerRef.current) {
        clearTimeout(distanceSaveTimerRef.current);
      }
    };
  }, []);

  const persistDistance = useCallback(
    (nextUeUnits: number | null) => {
      if (!brandingSpotId) {
        return;
      }

      if (lastSavedDistanceUeRef.current === nextUeUnits) {
        return;
      }

      lastSavedDistanceUeRef.current = nextUeUnits;
      setAutosaveStatus('saving');

      saveSpotDistance(nextUeUnits)
        .then(() => {
          setAutosaveStatus('saved');
        })
        .catch((saveError: Error) => {
          console.error(`Failed to save spot distance: ${saveError?.message}`, {
            brandingSpotId,
          });
          setAutosaveStatus('error');
        });
    },
    [brandingSpotId, saveSpotDistance, setAutosaveStatus],
  );

  const scheduleDistanceSave = useCallback(
    (nextUeUnits: number | null) => {
      if (distanceSaveTimerRef.current) {
        clearTimeout(distanceSaveTimerRef.current);
      }
      distanceSaveTimerRef.current = setTimeout(() => {
        distanceSaveTimerRef.current = null;
        persistDistance(nextUeUnits);
      }, DISTANCE_SAVE_DEBOUNCE_MS);
    },
    [persistDistance],
  );

  const handleDistanceChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const meters = Array.isArray(value) ? value[0] : value;
      setDistanceMeters(meters);
      if (!isDistanceInfinite) {
        scheduleDistanceSave(metersToUeUnits(meters));
      }
    },
    [isDistanceInfinite, scheduleDistanceSave],
  );

  const handleToggleInfiniteDistance = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setIsDistanceInfinite(checked);
      if (checked) {
        scheduleDistanceSave(null);
      } else {
        scheduleDistanceSave(metersToUeUnits(distanceMeters));
      }
    },
    [distanceMeters, scheduleDistanceSave],
  );

  const showDistanceSection = isVideo && !!brandingSpotId;

  const handleUploadImage = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleUploadVideo = useCallback(() => {
    videoFileInputRef.current?.click();
  }, [videoFileInputRef]);

  const handleUploadPreview = useCallback(() => {
    previewImageFileInputRef.current?.click();
  }, [previewImageFileInputRef]);

  const handleDelete = useCallback(() => {
    console.warn('Delete spot UI not wired yet', { ueIdentifier });
  }, [ueIdentifier]);

  const handleSelectType = (type: 'IMAGE' | 'VIDEO') => {
    if (isVideoStatic) {
      return;
    }
    setSelectedCustomizationSpotType(type);
  };

  const handleEditAssistant = useCallback(() => {
    const tag = selectedCustomizationTag || ueIdentifier;
    if (tag && selectedCustomizationTag !== tag) {
      setSelectedCustomizationTag(tag);
    }
    setShowCustomizeSpotModal(true);
    clearSelection();
  }, [
    selectedCustomizationTag,
    setSelectedCustomizationTag,
    ueIdentifier,
    setShowCustomizeSpotModal,
    clearSelection,
  ]);

  const handleOpenVideoLightbox = useCallback(() => {
    if (videoSrc) {
      setLightboxMode('video');
    }
  }, [videoSrc]);

  const handleOpenImageLightbox = useCallback((src: string | null) => {
    if (src) {
      setLightboxMode('image');
    }
  }, []);

  const isBusy = isUploading || isApplyingToSurface;
  const progressLabel =
    uploadStepMessage ||
    (isApplyingToSurface
      ? translate('editorMode.applyingToSurface', 'Applying to surface…')
      : `${uploadProgress}%`);
  const mediaFilename = selectedFile?.name || filename;
  const videoFilename =
    selectedVideoFile?.name || (isVideoSpot ? filename : '');
  const hasVideoSource = !!videoSrc;
  const hasPreviewSource = !!previewThumb;
  const previewStatusLabel = videoPreviewLocalUrl
    ? translate('editorMode.previewSelected', 'Selected')
    : previewImageUrl
    ? translate('editorMode.previewLoaded', 'Uploaded')
    : translate('editorMode.previewRequired', 'Required');
  const lightboxImageSrc =
    lightboxMode === 'image' ? previewThumb || imageOnlyThumb : null;

  return (
    <Box>
      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {translate('editorMode.type', 'Type')}
        </Typography>
        <Box sx={styles.segmented}>
          <Box
            sx={[
              styles.segmentedItem,
              ...(!isVideo ? [styles.segmentedItemActive] : []),
              ...(isVideoStatic ? [styles.segmentedItemDisabled] : []),
            ]}
            onClick={() => handleSelectType('IMAGE')}
            aria-disabled={isVideoStatic}
          >
            {translate('editorMode.image', 'Image')}
          </Box>
          <Box
            sx={[
              styles.segmentedItem,
              ...(isVideo ? [styles.segmentedItemActive] : []),
              ...(isVideoStatic ? [styles.segmentedItemLocked] : []),
            ]}
            onClick={() => handleSelectType('VIDEO')}
          >
            {translate('editorMode.video', 'Video')}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.fieldLabel}>
          {isVideo
            ? translate('editorMode.videoUploadHeader', 'Video & preview')
            : translate('editorMode.imageUploadHeader', 'Image')}
        </Typography>

        {isVideo ? (
          <Box sx={styles.mediaCard}>
            <Box sx={styles.compactRow}>
              <Tooltip
                title={
                  hasVideoSource
                    ? translate('editorMode.viewVideo', 'View video')
                    : ''
                }
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!hasVideoSource ? [styles.spotThumbPlaceholder] : []),
                    ...(hasVideoSource ? [styles.spotThumbClickable] : []),
                  ]}
                  onClick={handleOpenVideoLightbox}
                >
                  {hasVideoSource ? (
                    <Box sx={styles.spotThumbVideoBadge}>
                      <PlayArrowOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                  ) : (
                    <PlayArrowOutlinedIcon
                      sx={{ fontSize: 22, opacity: 0.7 }}
                    />
                  )}
                  {hasVideoSource && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Tooltip
                  title={videoFilename || ''}
                  placement="bottom"
                  enterDelay={400}
                >
                  <Typography sx={styles.compactTitle}>
                    {videoFilename ||
                      translate('editorMode.noVideoUploaded', 'No video yet')}
                  </Typography>
                </Tooltip>
                <Typography sx={styles.compactSubtitle}>
                  {translate('editorMode.video', 'Video')}
                  {videoFilename ? ' · WebM' : ''}
                </Typography>
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate('editorMode.uploadVideo', 'Upload video')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadVideo}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadVideo',
                        'Upload video',
                      )}
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={translate('editorMode.delete', 'Delete')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={[styles.iconSquareButton, styles.iconDangerButton]}
                      onClick={handleDelete}
                      disabled={isBusy}
                      aria-label={translate('editorMode.delete', 'Delete')}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {isBusy && (
              <Box sx={styles.progressBlock}>
                <LinearProgress
                  variant={
                    isApplyingToSurface ? 'indeterminate' : 'determinate'
                  }
                  value={uploadProgress}
                  sx={styles.compactProgressBar}
                />
                <Box sx={styles.progressTextRow}>
                  <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
                  <Typography sx={styles.conversionText}>
                    {progressLabel}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={styles.compactDivider} />

            <Box sx={styles.compactRow}>
              <Tooltip
                title={
                  hasPreviewSource
                    ? translate('editorMode.viewPreview', 'View preview image')
                    : ''
                }
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!hasPreviewSource ? [styles.spotThumbPlaceholder] : []),
                    ...(hasPreviewSource ? [styles.spotThumbClickable] : []),
                  ]}
                  style={
                    previewThumb
                      ? { backgroundImage: `url("${previewThumb}")` }
                      : undefined
                  }
                  onClick={() => handleOpenImageLightbox(previewThumb)}
                >
                  {!hasPreviewSource && (
                    <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                  )}
                  {hasPreviewSource && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Typography sx={styles.compactTitle}>
                  {translate('editorMode.previewImage', 'Preview image')}
                </Typography>
                <Box sx={styles.previewStatusRow}>
                  {hasPreviewSource && (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 12, color: '#4CCBA0' }}
                    />
                  )}
                  <Typography
                    sx={[
                      styles.compactSubtitle,
                      ...(hasPreviewSource ? [styles.previewStatusOk] : []),
                      ...(!hasPreviewSource
                        ? [styles.previewStatusRequired]
                        : []),
                    ]}
                  >
                    {previewStatusLabel}
                  </Typography>
                </Box>
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate(
                    'editorMode.uploadPreview',
                    'Upload preview image',
                  )}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadPreview}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadPreview',
                        'Upload preview image',
                      )}
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={styles.mediaCard}>
            <Box sx={styles.compactRow}>
              <Tooltip
                title={
                  imageOnlyThumb
                    ? translate('editorMode.viewLarger', 'View larger')
                    : ''
                }
                placement="bottom"
                enterDelay={400}
              >
                <Box
                  sx={[
                    styles.compactThumb,
                    ...(!imageOnlyThumb ? [styles.spotThumbPlaceholder] : []),
                    ...(imageOnlyThumb ? [styles.spotThumbClickable] : []),
                  ]}
                  style={
                    imageOnlyThumb
                      ? { backgroundImage: `url("${imageOnlyThumb}")` }
                      : undefined
                  }
                  onClick={() => handleOpenImageLightbox(imageOnlyThumb)}
                >
                  {!imageOnlyThumb && (
                    <ImageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                  )}
                  {!!imageOnlyThumb && (
                    <Box sx={styles.thumbExpandBadge}>
                      <OpenInFullOutlinedIcon sx={{ fontSize: 10 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>

              <Box sx={styles.compactInfo}>
                <Tooltip
                  title={mediaFilename || ''}
                  placement="bottom"
                  enterDelay={400}
                >
                  <Typography sx={styles.compactTitle}>
                    {mediaFilename ||
                      translate('editorMode.noMedia', 'No media set yet')}
                  </Typography>
                </Tooltip>
                <Typography sx={styles.compactSubtitle}>
                  {translate('editorMode.image', 'Image')}
                </Typography>
                {isBusy && (
                  <Box sx={styles.progressTextRow}>
                    <CircularProgress size={11} sx={{ color: '#AEB6FF' }} />
                    <Typography sx={styles.conversionText}>
                      {progressLabel}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={styles.compactActions}>
                <Tooltip
                  title={translate('editorMode.uploadImage', 'Upload image')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconSquareButton}
                      onClick={handleUploadImage}
                      disabled={isBusy}
                      aria-label={translate(
                        'editorMode.uploadImage',
                        'Upload image',
                      )}
                    >
                      <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={translate('editorMode.delete', 'Delete')}
                  placement="bottom"
                  enterDelay={400}
                >
                  <span>
                    <IconButton
                      size="small"
                      sx={[styles.iconSquareButton, styles.iconDangerButton]}
                      onClick={handleDelete}
                      disabled={isBusy}
                      aria-label={translate('editorMode.delete', 'Delete')}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {isBusy && (
              <Box sx={styles.progressBlock}>
                <LinearProgress
                  variant={
                    isApplyingToSurface ? 'indeterminate' : 'determinate'
                  }
                  value={uploadProgress}
                  sx={styles.compactProgressBar}
                />
              </Box>
            )}
          </Box>
        )}

        {isVideo && (
          <>
            <Typography sx={{ ...styles.spotFilenameMeta, padding: '0 4px' }}>
              {translate(
                'editorMode.videoFormatsHint',
                'Preview image is required. Supported PNG, JPG, JPEG. For video — MP4/MOV (automatic conversion to WebM).',
              )}
            </Typography>
            <Box sx={styles.videoConfirmRow}>
              <Button
                size="small"
                variant="contained"
                sx={styles.videoConfirmButton}
                disabled={!isVideoReadyToSubmit || isBusy}
                onClick={handleConfirmVideo}
              >
                {translate('editorMode.saveVideo', 'Save video')}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {showDistanceSection && (
        <Box sx={styles.section}>
          <Box sx={styles.distanceHeader}>
            <Typography sx={styles.fieldLabel}>
              {translate('editorMode.autoPlayDistance', 'AutoPlay Distance')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isDistanceInfinite}
                  onChange={handleToggleInfiniteDistance}
                />
              }
              label={
                <Box sx={styles.infiniteLabel}>
                  <Typography component="span" sx={styles.infiniteSymbol}>
                    ∞
                  </Typography>
                  <Typography component="span" sx={styles.infiniteText}>
                    {translate('editorMode.distanceInfinite', 'Infinite')}
                  </Typography>
                </Box>
              }
              sx={styles.distanceInfiniteToggle}
            />
          </Box>
          {!isDistanceInfinite && (
            <Box sx={styles.distanceSliderRow}>
              <Slider
                size="small"
                min={DISTANCE_MIN_METERS}
                max={DISTANCE_MAX_METERS}
                step={1}
                value={distanceMeters}
                onChange={handleDistanceChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) =>
                  translate('editorMode.distanceUnitM', '{{value}} m', {
                    value: v,
                  })
                }
                sx={styles.distanceSlider}
              />
              <Typography sx={styles.distanceValue}>
                {translate('editorMode.distanceUnitM', '{{value}} m', {
                  value: distanceMeters,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box sx={styles.assistantSection}>
        <RecordVoiceOverOutlinedIcon
          sx={{ fontSize: 22, color: 'rgba(255,255,255,0.7)' }}
        />
        <Box sx={styles.assistantSectionText}>
          <Typography sx={styles.assistantSectionTitle}>
            {translate('editorMode.assistantTitle', 'Assistant')}
          </Typography>
          <Typography sx={styles.assistantSectionSubtitle}>
            {translate(
              'editorMode.assistantSubtitle',
              'Configure the AI assistant for this spot',
            )}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          sx={styles.assistantSectionButton}
          onClick={handleEditAssistant}
        >
          {translate('editorMode.editAssistant', 'Edit')}
        </Button>
      </Box>

      <Modal
        open={lightboxMode !== null}
        onClose={() => setLightboxMode(null)}
        sx={styles.lightboxBackdrop}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
          }}
          onClick={() => setLightboxMode(null)}
        >
          {lightboxMode === 'video' && videoSrc ? (
            <Box
              component="video"
              src={videoSrc}
              controls
              autoPlay
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : lightboxImageSrc ? (
            <Box
              component="img"
              src={lightboxImageSrc}
              alt={filename || ueIdentifier}
              sx={styles.lightboxImg}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          ) : null}
          <IconButton
            onClick={() => setLightboxMode(null)}
            sx={styles.lightboxClose}
            size="small"
            aria-label={translate('editorMode.close', 'Close')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => handleImageFiles(e.target.files)}
      />
      <input
        ref={videoFileInputRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime"
        style={{ display: 'none' }}
        onChange={(e) => handleVideoFileSelect(e.target.files)}
      />
      <input
        ref={previewImageFileInputRef}
        type="file"
        accept="image/png,image/jpeg"
        style={{ display: 'none' }}
        onChange={(e) => handlePreviewImageFileSelect(e.target.files)}
      />
    </Box>
  );
};

export default SpotInspector;
